// src/views/WeeklyChallengeCompleteView.jsx
import React, { useMemo, useEffect } from 'react';
import {
  Trophy,
  CheckCircle2,
  Zap,
  Home,
  RotateCcw,
  Sparkles,
  ArrowRight,
  X
} from 'lucide-react';
import Mascot from '../components/Mascot';
import ConfettiEffect from '../components/effects/ConfettiEffect';
import {
  getWeeklyChallengeProgress,
  clearWeeklyChallengeProgress
} from '../utils/weeklyChallengeStorage';

export default function WeeklyChallengeCompleteView({
  challenge,
  setView,
  userProgress,
  onRestartChallenge,
  onExit
}) {
  const activeSkin = userProgress?.currentSkin || 'bribark';

  // Bloquear el scroll de fondo mientras el modal esté montado
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const progress = useMemo(
    () => getWeeklyChallengeProgress(challenge?.id),
    [challenge?.id]
  );

  const sections = challenge?.sections || [];

  // Métricas agregadas
  const stats = useMemo(() => {
    let totalScore = 0;
    let totalQuestions = 0;

    sections.forEach((sec) => {
      const data = progress.sections?.[sec.id];
      if (data) {
        totalScore += Number(data.score || 0);
        totalQuestions += Number(data.total || sec.totalQuestions || 0);
      }
    });

    const safeTotal = totalQuestions > 0 ? totalQuestions : 1;
    const avgAccuracy = Math.round((totalScore / safeTotal) * 100);

    return {
      totalScore,
      totalQuestions,
      avgAccuracy
    };
  }, [sections, progress]);

  const handleReset = () => {
    if (window.confirm('¿Deseas reiniciar este desafío semanal y realizarlo nuevamente?')) {
      clearWeeklyChallengeProgress(challenge?.id);
      if (onRestartChallenge) onRestartChallenge();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-0 sm:p-3 animate-in fade-in duration-300 select-none"
      style={{ overscrollBehavior: 'contain' }}
    >
      <ConfettiEffect />

      <div className="w-full max-w-lg h-full sm:h-[94vh] sm:max-h-[850px] sm:rounded-[2.5rem] bg-gradient-to-b from-slate-50 via-white to-slate-50 flex flex-col overflow-hidden shadow-2xl relative border-0 sm:border sm:border-slate-200">
        
        {/* Header con botón cerrar */}
        <header className="shrink-0 px-4 py-3 bg-white border-b border-slate-100 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
            Resumen Semanal
          </span>
          <button
            onClick={onExit || (() => setView('lobby'))}
            className="p-1.5 rounded-xl bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </header>

        {/* Contenido scrolleable */}
        <main className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-5 text-center flex flex-col items-center justify-between">
          
          {/* Encabezado celebratorio */}
          <div className="space-y-1.5 pt-1">
            <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 border-2 border-amber-300 px-3.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest animate-bounce shadow-sm">
              <Sparkles size={12} className="text-amber-600" /> ¡Desafío Semanal Superado!
            </div>

            <h1 className="text-2xl font-black text-slate-800 tracking-tight leading-tight">
              {challenge?.title || 'Desafío Semanal'}
            </h1>

            <p className="text-xs text-slate-500 font-bold max-w-xs mx-auto">
              Has completado todas las etapas de lectura, gramática y vocabulario de la semana.
            </p>
          </div>

          {/* Mascota con trofeo */}
          <div className="relative flex flex-col items-center justify-center my-1">
            <div className="absolute -top-3 right-4 bg-yellow-400 text-yellow-950 p-2 rounded-xl shadow-md border-2 border-white animate-pulse">
              <Trophy size={20} />
            </div>
            <div className="scale-95 drop-shadow-xl">
              <Mascot
                type={activeSkin}
                emotion="happy"
                message="¡Impresionante! Has dominado el reto semanal por completo. 🔥✨"
                isJumping={true}
              />
            </div>
          </div>

          {/* Resumen de puntuación */}
          <div className="w-full bg-white rounded-2xl p-4 shadow-md border-2 border-slate-100 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-indigo-50/80 rounded-xl p-3 border border-indigo-100">
                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-400 block mb-0.5">
                  Aciertos Totales
                </span>
                <span className="text-xl font-black text-indigo-700">
                  {stats.totalScore} / {stats.totalQuestions}
                </span>
              </div>

              <div className="bg-emerald-50/80 rounded-xl p-3 border border-emerald-100">
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-500 block mb-0.5">
                  Efectividad
                </span>
                <span className="text-xl font-black text-emerald-700">
                  {stats.avgAccuracy}%
                </span>
              </div>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-amber-100/60 rounded-xl p-2.5 border border-amber-200 flex items-center justify-center gap-2">
              <Zap size={16} className="text-amber-500 fill-amber-500" />
              <span className="text-xs font-black text-amber-800">
                +{challenge?.xpReward || 350} XP ganados
              </span>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="w-full space-y-2 pt-1">
            <button
              onClick={onExit || (() => setView('lobby'))}
              className="w-full py-3.5 bg-indigo-600 text-white font-black text-sm rounded-2xl shadow-lg shadow-indigo-200 border-b-4 border-indigo-900 hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <Home size={16} /> Volver al Inicio
            </button>

            <button
              onClick={handleReset}
              className="w-full py-2.5 bg-slate-100 text-slate-600 font-bold text-xs rounded-xl hover:bg-slate-200 active:scale-95 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <RotateCcw size={13} /> Reiniciar desafío
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
